import { useState, useEffect, useCallback } from 'react';
import { X, Activity, Wifi, Video, Mic, ArrowDown, ArrowUp, RefreshCw } from 'lucide-react';
import { useRoom } from '../../contexts/RoomContext';
import { useParticipantsStore } from '../../stores/participantsStore';

interface Stats {
  producers: any[];
  consumers: any[];
  sendTransport: any;
  recvTransport: any;
}

interface WebRTCMonitorProps {
  isOpen: boolean;
  onClose: () => void;
}

const WebRTCMonitor = ({ isOpen, onClose }: WebRTCMonitorProps) => {
  const { mediasoup, socket } = useRoom();
  const { participants, localParticipant } = useParticipantsStore();
  const [stats, setStats] = useState<Stats | null>(null);
  const [quickStatus, setQuickStatus] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const refreshStats = useCallback(async () => {
    if (!mediasoup) return;
    
    try {
      const newStats = await mediasoup.getStats();
      setStats(newStats);
      setQuickStatus(mediasoup.getQuickStatus());
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to get stats:', error);
    }
  }, [mediasoup]);

  useEffect(() => {
    if (!isOpen || !autoRefresh) return;
    
    refreshStats();
    const interval = setInterval(refreshStats, 2000);
    return () => clearInterval(interval);
  }, [isOpen, autoRefresh, refreshStats]);

  if (!isOpen) return null;

  const getConnectionStateColor = (state: string | null) => {
    switch (state) {
      case 'connected': return 'text-green-500';
      case 'connecting': return 'text-yellow-500';
      case 'disconnected': return 'text-red-500';
      case 'failed': return 'text-red-600';
      case 'closed': return 'text-gray-500';
      default: return 'text-gray-400';
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTrackInfo = (statsArray: any[]): { bitrate: number; packetsLost: number; jitter: number } => {
    let bitrate = 0;
    let packetsLost = 0;
    let jitter = 0;

    for (const stat of statsArray) {
      if (stat.type === 'outbound-rtp' || stat.type === 'inbound-rtp') {
        if (stat.bytesSent) {
          bitrate = stat.bytesSent;
        }
        if (stat.bytesReceived) {
          bitrate = stat.bytesReceived;
        }
        if (stat.packetsLost) {
          packetsLost = stat.packetsLost;
        }
        if (stat.jitter) {
          jitter = stat.jitter * 1000; // Convert to ms
        }
      }
    }

    return { bitrate, packetsLost, jitter };
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-bold text-white">WebRTC Monitor</h2>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-400">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              Auto-refresh
            </label>
            <button
              onClick={refreshStats}
              className="p-2 hover:bg-gray-700 rounded-lg transition"
            >
              <RefreshCw className="w-5 h-5 text-gray-400" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-6">
          {/* Connection Status */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Wifi className="w-5 h-5" />
              Connection Status
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-1">Socket</div>
                <div className={`font-medium ${socket?.connected ? 'text-green-500' : 'text-red-500'}`}>
                  {socket?.connected ? 'Connected' : 'Disconnected'}
                </div>
                <div className="text-xs text-gray-500 mt-1">{socket?.id || 'N/A'}</div>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-1 flex items-center gap-1">
                  <ArrowUp className="w-4 h-4" /> Send Transport
                </div>
                <div className={`font-medium ${getConnectionStateColor(quickStatus?.sendTransportState)}`}>
                  {quickStatus?.sendTransportState || 'N/A'}
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-1 flex items-center gap-1">
                  <ArrowDown className="w-4 h-4" /> Recv Transport
                </div>
                <div className={`font-medium ${getConnectionStateColor(quickStatus?.recvTransportState)}`}>
                  {quickStatus?.recvTransportState || 'N/A'}
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-1">Last Update</div>
                <div className="font-medium text-white">
                  {lastUpdate ? lastUpdate.toLocaleTimeString() : 'Never'}
                </div>
              </div>
            </div>
          </section>

          {/* Producers (Outgoing) */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <ArrowUp className="w-5 h-5 text-green-400" />
              Producers (Outgoing) - {stats?.producers.length || 0}
            </h3>
            {stats?.producers.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-4 text-gray-400 text-center">
                No active producers
              </div>
            ) : (
              <div className="space-y-2">
                {stats?.producers.map((producer) => {
                  const trackInfo = producer.stats ? getTrackInfo(producer.stats) : null;
                  return (
                    <div key={producer.id} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {producer.kind === 'video' ? (
                            <Video className="w-4 h-4 text-blue-400" />
                          ) : (
                            <Mic className="w-4 h-4 text-green-400" />
                          )}
                          <span className="font-medium text-white">{producer.kind}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {producer.paused && (
                            <span className="px-2 py-1 bg-yellow-600 text-xs rounded">PAUSED</span>
                          )}
                          {producer.closed && (
                            <span className="px-2 py-1 bg-red-600 text-xs rounded">CLOSED</span>
                          )}
                          <span className={`px-2 py-1 text-xs rounded ${
                            producer.track?.readyState === 'live' ? 'bg-green-600' : 'bg-gray-600'
                          }`}>
                            {producer.track?.readyState || 'unknown'}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 font-mono truncate">ID: {producer.id}</div>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                        <div>
                          <span className="text-gray-400">Track enabled:</span>{' '}
                          <span className={producer.track?.enabled ? 'text-green-400' : 'text-red-400'}>
                            {String(producer.track?.enabled)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400">Muted:</span>{' '}
                          <span className={!producer.track?.muted ? 'text-green-400' : 'text-red-400'}>
                            {String(producer.track?.muted)}
                          </span>
                        </div>
                        {trackInfo && (
                          <div>
                            <span className="text-gray-400">Sent:</span>{' '}
                            <span className="text-white">{formatBytes(trackInfo.bitrate)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Consumers (Incoming) */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <ArrowDown className="w-5 h-5 text-blue-400" />
              Consumers (Incoming) - {stats?.consumers.length || 0}
            </h3>
            {stats?.consumers.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-4 text-gray-400 text-center">
                No active consumers
              </div>
            ) : (
              <div className="space-y-2">
                {stats?.consumers.map((consumer) => {
                  const trackInfo = consumer.stats ? getTrackInfo(consumer.stats) : null;
                  return (
                    <div key={consumer.id} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {consumer.kind === 'video' ? (
                            <Video className="w-4 h-4 text-blue-400" />
                          ) : (
                            <Mic className="w-4 h-4 text-green-400" />
                          )}
                          <span className="font-medium text-white">{consumer.kind}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {consumer.paused && (
                            <span className="px-2 py-1 bg-yellow-600 text-xs rounded">PAUSED</span>
                          )}
                          {consumer.closed && (
                            <span className="px-2 py-1 bg-red-600 text-xs rounded">CLOSED</span>
                          )}
                          <span className={`px-2 py-1 text-xs rounded ${
                            consumer.track?.readyState === 'live' ? 'bg-green-600' : 'bg-gray-600'
                          }`}>
                            {consumer.track?.readyState || 'unknown'}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 font-mono truncate">
                        Consumer: {consumer.id}
                      </div>
                      <div className="text-xs text-gray-500 font-mono truncate">
                        Producer: {consumer.producerId}
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                        <div>
                          <span className="text-gray-400">Track enabled:</span>{' '}
                          <span className={consumer.track?.enabled ? 'text-green-400' : 'text-red-400'}>
                            {String(consumer.track?.enabled)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400">Muted:</span>{' '}
                          <span className={!consumer.track?.muted ? 'text-green-400' : 'text-red-400'}>
                            {String(consumer.track?.muted)}
                          </span>
                        </div>
                        {trackInfo && (
                          <div>
                            <span className="text-gray-400">Received:</span>{' '}
                            <span className="text-white">{formatBytes(trackInfo.bitrate)}</span>
                          </div>
                        )}
                      </div>
                      {trackInfo && trackInfo.packetsLost > 0 && (
                        <div className="mt-2 text-sm">
                          <span className="text-red-400">Packets lost: {trackInfo.packetsLost}</span>
                          {trackInfo.jitter > 0 && (
                            <span className="text-yellow-400 ml-4">Jitter: {trackInfo.jitter.toFixed(2)}ms</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Participants Streams */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3">Participants Streams</h3>
            <div className="space-y-2">
              {localParticipant && (
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-white">
                      {localParticipant.displayName} (You)
                    </span>
                    <span className={`px-2 py-1 text-xs rounded ${
                      localParticipant.stream ? 'bg-green-600' : 'bg-red-600'
                    }`}>
                      {localParticipant.stream ? 'Has Stream' : 'No Stream'}
                    </span>
                  </div>
                  {localParticipant.stream && (
                    <div className="text-xs text-gray-400 mt-2">
                      Tracks: {localParticipant.stream.getTracks().map(t => `${t.kind}(${t.readyState})`).join(', ')}
                    </div>
                  )}
                </div>
              )}
              
              {Array.from(participants.values()).map((participant) => (
                <div key={participant.id} className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-white">{participant.displayName}</span>
                    <span className={`px-2 py-1 text-xs rounded ${
                      participant.stream ? 'bg-green-600' : 'bg-red-600'
                    }`}>
                      {participant.stream ? 'Has Stream' : 'No Stream'}
                    </span>
                  </div>
                  {participant.stream && (
                    <div className="text-xs text-gray-400 mt-2">
                      Tracks: {participant.stream.getTracks().map(t => `${t.kind}(${t.readyState})`).join(', ')}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">ID: {participant.id}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Raw Stats (Debug) */}
          <section>
            <details className="bg-gray-800 rounded-lg">
              <summary className="p-4 cursor-pointer text-gray-400 hover:text-white">
                Raw Stats (Full JSON)
              </summary>
              <pre className="p-4 text-xs text-gray-300 overflow-auto max-h-96">
                {JSON.stringify(stats, null, 2)}
              </pre>
            </details>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WebRTCMonitor;
